import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Button } from '@carbon/react';
import { Checkmark, Filter, Link } from '@carbon/react/icons';
import { CrudFilterChip } from './CrudFilterChip';
import { CrudPanelShell } from './CrudPanelShell';
import { CrudViewsPanel } from './CrudViewsPanel';
import { CrudFilterField } from './defineResource';
import { defaultOperator } from './filterTypes';
import { useCrudConfig } from './provider';
import { CrudUrlState, FilterState, FilterValue } from './urlState';
import { useDismissPanel } from './useDismissPanel';

export interface CrudFilterBarProps {
  filterable: CrudFilterField[];
  filters: FilterState;
  onChange: (next: FilterState) => void;
  /**
   * Where to render the `Add filter` control. Given a node, the control is portalled there
   * — the toolbar row — instead of sitting at the end of the chip row. Omit it and the bar
   * renders exactly as it did before this prop existed.
   */
  addSlot?: HTMLElement | null;
  /**
   * Saved-view wiring (spec §3b): given, the popover renders the views panel above the
   * chips (menu mode only — a caller with no addSlot gets the bar it always got). All three
   * fields travel together in one object rather than as separate props, so a caller who
   * wires two of three and forgets the last gets a type error instead of a silently
   * missing panel. Omitted entirely, the bar behaves exactly as it did before views
   * existed. `filterableFields` is not part of this — it is derived from `filterable`
   * above, the one source of truth for what this resource can filter on.
   */
  views?: {
    resourceKey: string;
    /** The list's current URL state (page/search/sort/filters), for the panel's Save capture. */
    urlState: CrudUrlState;
    /** Writes an applied saved view's state back through the caller's URL binding. */
    onApply: (state: CrudUrlState) => void;
  };
}

/** How many wrapped rows of chips the bar shows before collapsing the rest behind `+N more`. */
const MAX_ROWS = 2;

/**
 * How many chips fall past `maxRows` wrapped rows, given each chip's offset from the top of
 * the bar. Rows are the DISTINCT offsets — deriving them from geometry rather than a fixed
 * chip count is what lets the cut follow the container's width instead of guessing at it.
 *
 * Pure and exported because jsdom has no layout (every offsetTop is 0), so this is the only
 * part of the collapse that a unit test can actually exercise.
 */
export function countChipsBeyondRow(tops: number[], maxRows: number): number {
  const rows = Array.from(new Set(tops)).sort((a, b) => a - b);
  if (rows.length <= maxRows) {
    return 0;
  }
  const firstHiddenRowTop = rows[maxRows];
  return tops.filter((top) => top >= firstHiddenRowTop).length;
}

/**
 * A chip is committed once it says something: `isEmpty` is its own predicate, every other
 * operator needs a non-empty value. Until then it is the bar's local DRAFT — rendered for
 * editing, but never written to the parent, because the URL codec skips empty chips and
 * an unfinished chip must not touch the URL or the query (spec §7.4).
 */
function isCommitted(chip: FilterValue): boolean {
  if (chip.op === 'isEmpty') {
    return true;
  }
  const parts = Array.isArray(chip.value) ? chip.value : [chip.value];
  return parts.some((part) => part !== '' && part != null);
}

function sameFilterState(a: FilterState, b: FilterState): boolean {
  const keys = Object.keys(a);
  if (keys.length !== Object.keys(b).length) {
    return false;
  }
  return keys.every((key) => {
    const chipA = a[key];
    const chipB = b[key];
    if (!chipB || chipA.op !== chipB.op) {
      return false;
    }
    const valuesA = Array.isArray(chipA.value) ? chipA.value : [chipA.value];
    const valuesB = Array.isArray(chipB.value) ? chipB.value : [chipB.value];
    return valuesA.length === valuesB.length && valuesA.every((value, index) => value === valuesB[index]);
  });
}

/**
 * The filter chip row (spec §7.4): one chip per active entry plus an `Add filter` BUTTON
 * whose panel lists the fields not yet in use. All stock Carbon — no Popover, no Menu (§3
 * #29) — so the panel is a plain div with its own dismissal (see `useDismissPanel`).
 *
 * The bar carries the empty-value chips the URL cannot: a freshly added chip is a local
 * draft, committed to `onChange` the moment it gains a value (or an isEmpty operator).
 * External changes to `filters` (Clear, a shared link) drop the drafts.
 */
export function CrudFilterBar({ filterable, filters, onChange, addSlot, views }: CrudFilterBarProps) {
  const { translate } = useCrudConfig();
  const [drafts, setDrafts] = useState<FilterState>({});
  const barRef = useRef<HTMLDivElement>(null);
  const [hiddenCount, setHiddenCount] = useState(0);
  const [expanded, setExpanded] = useState(false);

  // Menu mode: the bar lives in a popover behind a counted trigger in the toolbar, rather
  // than laid out along the row. `addSlot` is what selects it — a caller that supplies a
  // slot wants the compact presentation.
  const inMenu = Boolean(addSlot);
  const menuRef = useRef<HTMLDivElement>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  useDismissPanel(
    menuOpen,
    menuRef,
    useCallback(() => setMenuOpen(false), [])
  );

  // In menu mode edits are STAGED: the table keeps its current rows while chips are being
  // arranged, and one Apply refetches once instead of on every keystroke and operator
  // change. Inline mode is unchanged — it has no footer to commit from.
  const [staged, setStaged] = useState<FilterState>(filters);

  // Transient feedback for the copy-link button: 'idle' -> 'copied' | 'failed' -> back to
  // 'idle' after ~2s. A ref (not state) holds the timer so scheduling a new one can clear
  // whatever is still pending, and so the cleanup effect can cancel it on unmount — a
  // `setCopyStatus` firing after the panel closes would be a no-op update on an unmounted
  // component.
  const [copyStatus, setCopyStatus] = useState<'idle' | 'copied' | 'failed'>('idle');
  const copyResetTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (copyResetTimer.current) {
        clearTimeout(copyResetTimer.current);
      }
    },
    []
  );

  const copyLink = useCallback(() => {
    // `writeText` is undefined in jsdom and in non-secure contexts — that absence is a
    // failure to report, not a silent no-op, so it is normalized into the same rejected
    // promise the catch below already handles.
    const clipboard = navigator.clipboard;
    const written = clipboard ? clipboard.writeText(window.location.href) : Promise.reject();
    written
      .then(() => setCopyStatus('copied'))
      .catch(() => setCopyStatus('failed'))
      .finally(() => {
        if (copyResetTimer.current) {
          clearTimeout(copyResetTimer.current);
        }
        copyResetTimer.current = setTimeout(() => setCopyStatus('idle'), 2000);
      });
  }, []);

  /**
   * Measuring has a feedback trap: hiding chips changes the layout that decided which chips to
   * hide. So every chip is un-hidden for the read and restored immediately — inside a LAYOUT
   * effect, before paint, so nothing flickers. Only the resulting count reaches state, and
   * re-measuring an unchanged layout produces the same count, so React bails out of the loop.
   */
  const measure = useCallback(() => {
    const bar = barRef.current;
    if (!bar) {
      return;
    }
    const chipElements = Array.from(bar.querySelectorAll<HTMLElement>('.crud-filter-chip'));
    if (chipElements.length === 0) {
      setHiddenCount(0);
      return;
    }
    const restore = chipElements.filter((element) => element.hidden);
    restore.forEach((element) => {
      element.hidden = false;
    });
    const tops = chipElements.map((element) => element.offsetTop);
    restore.forEach((element) => {
      element.hidden = true;
    });
    setHiddenCount(countChipsBeyondRow(tops, MAX_ROWS));
  }, []);

  useLayoutEffect(measure);

  useEffect(() => {
    // Narrowing the window re-wraps the chips, so the cut has to be recomputed. jsdom (and
    // older browsers) have no ResizeObserver; there the bar simply keeps its initial count.
    if (typeof ResizeObserver === 'undefined' || !barRef.current) {
      return;
    }
    const observer = new ResizeObserver(measure);
    observer.observe(barRef.current);
    return () => observer.disconnect();
  }, [measure]);
  // The committed state these chips last produced. A `filters` prop that diverges from it
  // came from outside this component, and outside changes reset the local drafts.
  const lastPushedRef = useRef<FilterState>(filters);

  useEffect(() => {
    if (!sameFilterState(filters, lastPushedRef.current)) {
      lastPushedRef.current = filters;
      setDrafts({});
      setStaged(filters);
    }
  }, [filters]);

  const byField = new Map(filterable.map((field) => [field.field, field]));

  const push = (next: FilterState) => {
    if (inMenu) {
      setStaged(next);
      return;
    }
    lastPushedRef.current = next;
    onChange(next);
  };

  // What the chips read from. Inline mode has no staging layer, so it reads the committed
  // state directly and every edit is immediately live.
  const active = inMenu ? staged : filters;

  const setChip = (field: string, chip: FilterValue) => {
    if (isCommitted(chip)) {
      const { [field]: _draft, ...restDrafts } = drafts;
      setDrafts(restDrafts);
      push({ ...active, [field]: chip });
      return;
    }
    // Back to a draft: it must also LEAVE the committed state, or a cleared value would
    // keep filtering with its previous content.
    if (field in active) {
      const { [field]: _removed, ...restActive } = active;
      push(restActive);
    }
    setDrafts({ ...drafts, [field]: chip });
  };

  const removeChip = (field: string) => {
    if (field in drafts) {
      const { [field]: _draft, ...restDrafts } = drafts;
      setDrafts(restDrafts);
    }
    if (field in active) {
      const { [field]: _removed, ...restActive } = active;
      push(restActive);
    }
  };

  /**
   * One row per filterable field, always — there is no "add" step. A resource declares a
   * handful of filters, so listing them is shorter than a menu that reveals them one at a
   * time, and it answers "what can I filter by?" without a click.
   *
   * A field with no committed value and no draft still gets a row, seeded with its type's
   * default operator and an empty value. An empty value is what keeps it out of the
   * committed state, so an untouched row filters nothing.
   */
  const chips: Array<[string, FilterValue]> = filterable.map((field) => [
    field.field,
    active[field.field] ?? drafts[field.field] ?? { op: defaultOperator(field.type), value: '' },
  ]);

  // The chips past the cut stay MOUNTED and merely hidden: unmounting them would throw away
  // an open editor's focus and, more importantly, leave nothing for `measure` to read.
  // The collapse exists because a horizontal row runs out of width. The menu stacks its
  // chips vertically and can scroll, so every chip stays visible there — and the row-count
  // measurement would otherwise read one "row" per chip and hide all but the first two.
  const visibleCount = inMenu || expanded ? chips.length : chips.length - hiddenCount;

  const bar = (
    <div className="crud-filter-bar" ref={barRef}>
      {chips.map(([field, chip], index) => {
        const config = byField.get(field);
        if (!config) {
          return null;
        }
        return (
          <CrudFilterChip
            key={field}
            field={config}
            chip={chip}
            hidden={index >= visibleCount}
            onChange={(next) => setChip(field, next)}
            onRemove={() => removeChip(field)}
          />
        );
      })}
      {!inMenu && hiddenCount > 0 && (
        <Button kind="ghost" size="sm" onClick={() => setExpanded(!expanded)} aria-expanded={expanded}>
          {expanded
            ? translate('common.filters.showLess')
            : translate('common.filters.showMore', { count: hiddenCount })}
        </Button>
      )}
    </div>
  );

  if (!addSlot) {
    return bar;
  }

  // Committed filters only — not the staged ones. A staged edit has not reached the table
  // yet, and a draft chip has no value at all, so counting either would claim the list is
  // narrowed when it is not.
  const activeCount = Object.keys(filters).filter((field) => byField.has(field)).length;

  // What Apply would commit right now — a draft has no value, so sending it would compile
  // to nothing and the URL would carry a filter that does not filter. Shared by the footer
  // Apply and by Save (R17): Save must persist exactly what Apply would commit, not the
  // pre-edit narrowing.
  const committedStaged = (): FilterState =>
    Object.fromEntries(Object.entries(staged).filter(([field]) => byField.has(field))) as FilterState;

  const applyStaged = () => {
    const next = committedStaged();
    lastPushedRef.current = next;
    onChange(next);
    setMenuOpen(false);
  };

  // The saved-view panel renders only once `views` is wired — a caller that omits it gets
  // the bar exactly as it behaved before views existed. Views never carry a page (R15):
  // `current` is what Save would capture, page pinned to 1. `filterableFields` is derived
  // from `filterable`, not carried on `views`, so there is one source of truth for it.
  const viewsPanel = views ? (
    <CrudViewsPanel
      resourceKey={views.resourceKey}
      filterableFields={filterable.map((field) => field.field)}
      current={{ page: 1, search: views.urlState.search, sort: views.urlState.sort, filters: committedStaged() }}
      onApply={(state) => {
        views.onApply(state);
        setMenuOpen(false);
      }}
      onSave={() => applyStaged()}
    />
  ) : null;

  const menu = (
    <div className="crud-filter-menu" ref={menuRef}>
      <Button
        kind="ghost"
        size="lg"
        hasIconOnly
        renderIcon={Filter}
        iconDescription={
          activeCount > 0 ? `${translate('common.filters.title')} (${activeCount})` : translate('common.filters.title')
        }
        tooltipPosition="bottom"
        className="crud-filter-menu__trigger"
        aria-expanded={menuOpen}
        aria-haspopup="true"
        onClick={() => setMenuOpen(!menuOpen)}
      />
      {/* The count is in the trigger's accessible name above, so the badge is decorative. */}
      {activeCount > 0 && (
        <span className="crud-filter-menu__count" aria-hidden>
          {activeCount}
        </span>
      )}
      {menuOpen && (
        <CrudPanelShell
          className="crud-filter-menu__panel"
          title={translate('common.filters.title')}
          resetDisabled={chips.length === 0}
          onReset={() => {
            setStaged({});
            setDrafts({});
          }}
          onApply={applyStaged}
        >
          {viewsPanel}
          {/*
            The whole narrowing already lives in the URL (page, s, sort, f), so a
            filtered table is already addressable — this is the affordance, not
            the mechanism. It reads window.location.href — the last APPLIED URL —
            live, on click, rather than through `staged`/`push`: it deliberately
            ignores whatever is mid-edit in the chips below. Apply is what moves
            staged edits into the URL, so copying here always shares what the
            table is actually showing, never a draft the table hasn't refetched
            for yet.
          */}
          <div className="crud-filter-menu__share">
            <Button kind="ghost" size="sm" renderIcon={copyStatus === 'copied' ? Checkmark : Link} onClick={copyLink}>
              {copyStatus === 'copied'
                ? translate('actions.copyLinkCopied')
                : copyStatus === 'failed'
                ? translate('actions.copyLinkFailed')
                : translate('actions.copyLink')}
            </Button>
          </div>
          {bar}
        </CrudPanelShell>
      )}
    </div>
  );

  return createPortal(menu, addSlot);
}
